import { useMemo } from 'react';
import { css } from '@emotion/react';
import { OperatoryDetails } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { Icon } from '@frontend/icons';
import { useBreakpoint } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { NakedButton, PopoverMenu, usePopoverMenu } from '@frontend/design-system';
import { OperatoryAvatarInfo } from '../helpers';
import { OperatoryAvatar } from '../operatory-avatar';
import { AvatarColorList } from './avatar-color-list';
import { AvatarIconList } from './avatar-icon-list';

type OperatoryAvatarSelectorProps = {
  value: OperatoryAvatarInfo;
  onChange: (value: OperatoryAvatarInfo) => void;
};

export const OperatoryAvatarSelector = ({ value, onChange }: OperatoryAvatarSelectorProps) => {
  const size = useBreakpoint();
  const isLargeDevice = size === 'large';

  const { getMenuProps, getTriggerProps, isOpen } = usePopoverMenu({ placement: 'bottom-start' });

  const handleColorChange = (color: OperatoryAvatarInfo['color']) => onChange({ ...value, color });
  const handleIconChange = (icon: OperatoryAvatarInfo['icon']) => onChange({ ...value, icon });

  const operatoryAvatarInfo = useMemo<OperatoryDetails['icon']>(
    () => ({ color: value.color, name: value.icon }),
    [value]
  );

  return (
    <div css={{ marginTop: theme.spacing(0.5), maxWidth: theme.spacing(10) }}>
      <NakedButton {...getTriggerProps()} css={buttonStyles}>
        <OperatoryAvatar avatarInfo={operatoryAvatarInfo} />
        <Icon color='light' name={isOpen ? 'caret-up-small' : 'caret-down-small'} />
      </NakedButton>
      <PopoverMenu
        css={{ maxWidth: theme.spacing(isLargeDevice ? 45 : 31), padding: theme.spacing(2) }}
        {...getMenuProps()}
      >
        <AvatarColorList value={value.color} onChange={handleColorChange} />
        <AvatarIconList value={value.icon} onChange={handleIconChange} color={value.color} />
      </PopoverMenu>
    </div>
  );
};

const buttonStyles = css({
  alignItems: 'center',
  borderRadius: theme.borderRadius.small,
  border: `1px solid ${theme.colors.neutral30}`,
  display: 'flex',
  gap: theme.spacing(0.5),
  justifyContent: 'space-between',
  padding: theme.spacing(0.6, 1),
  width: '100%',
  '&:hover': {
    backgroundColor: theme.colors.neutral5,
  },
});
